import { useState, useCallback } from 'react';
import { TrendingUp } from 'lucide-react';
import {
  ComposedChart, LineChart, AreaChart, Area, Bar, Line,
  XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer,
  Tooltip as ChartTooltip,
} from 'recharts';
import { analyticsApi } from '@/api/analytics.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, fmtDate, fmtAmt, fmtAmtZero, fmtPct, Delta, UnavailableMetrics,
  Section, EmptyState, CHART_COLOURS,
} from '@/components/analytics/AnalyticsKit';
import type {
  AnalyticsGranularity, FinancialTrendDto, FinancialTrendFilter, FinancialTrendPoint,
} from '@/types/analytics.types';

// ── Local helpers ─────────────────────────────────────────────────────────────

const MONTHS_OPTIONS = [12, 24, 36];

const GRANULARITY_OPTIONS: { value: AnalyticsGranularity; label: string }[] = [
  { value: 'MONTH',   label: 'Monthly' },
  { value: 'QUARTER', label: 'Quarterly' },
  { value: 'YEAR',    label: 'Yearly' },
];

/** Axis ticks need to stay short or the chart gutter eats the plot area. */
function fmtCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000)     return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)         return `${(value / 1_000).toFixed(0)}K`;
  return value.toFixed(0);
}

// ── Chart tooltip ─────────────────────────────────────────────────────────────

const TrendTooltip = ({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-700 dark:text-gray-200 mb-2">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex items-center justify-between gap-6">
          <span className="flex items-center gap-1.5" style={{ color: p.color }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            {p.name}
          </span>
          <span className="font-medium tabular-nums text-gray-900 dark:text-white">
            {fmtAmtZero(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

// ── CAGR tile ─────────────────────────────────────────────────────────────────

/**
 * A null CAGR means the backend could not compute it (too few periods, or a
 * negative base). It is shown as an em dash — never as 0%, which would read as
 * "flat growth" and be a lie.
 */
const CagrTile = ({ label, value, note }: {
  label: string;
  value: number | null;
  note: string;
}) => {
  const unavailable = value === null;
  const tone = unavailable
    ? 'text-gray-400 dark:text-gray-600'
    : value >= 0
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-600 dark:text-red-400';

  return (
    <div className={cn(CARD_CLS, 'p-4 flex flex-col gap-1')}>
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      <span className={cn('text-2xl font-bold tabular-nums', tone)}>{fmtPct(value)}</span>
      <span className="text-[10px] leading-tight text-gray-400 dark:text-gray-500">
        {unavailable ? 'Not computable for this window' : note}
      </span>
    </div>
  );
};

// ── Excel export ──────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Period',           header: 'Period',            width: 14 },
  { key: 'Revenue',          header: 'Revenue',           width: 16, numFmt: '#,##0.00' },
  { key: 'Growth',          header: 'Growth %',          width: 12, numFmt: '#,##0.00' },
  { key: 'Expenses',         header: 'Expenses',          width: 16, numFmt: '#,##0.00' },
  { key: 'GrossProfit',      header: 'Gross Profit',      width: 16, numFmt: '#,##0.00' },
  { key: 'OperatingProfit',  header: 'Operating Profit',  width: 16, numFmt: '#,##0.00' },
  { key: 'NetProfit',        header: 'Net Profit',        width: 16, numFmt: '#,##0.00' },
  { key: 'RevenueMa3',       header: 'Revenue MA3',       width: 16, numFmt: '#,##0.00' },
  { key: 'NetProfitMa3',     header: 'Net Profit MA3',    width: 16, numFmt: '#,##0.00' },
  { key: 'Cash',             header: 'Cash',              width: 16, numFmt: '#,##0.00' },
  { key: 'AR',               header: 'Accounts Receivable', width: 16, numFmt: '#,##0.00' },
  { key: 'AP',               header: 'Accounts Payable',  width: 16, numFmt: '#,##0.00' },
  { key: 'WorkingCapital',   header: 'Working Capital',   width: 16, numFmt: '#,##0.00' },
  { key: 'RevenueYtd',       header: 'Revenue YTD',       width: 16, numFmt: '#,##0.00' },
  { key: 'NetProfitYtd',     header: 'Net Profit YTD',    width: 16, numFmt: '#,##0.00' },
];

function buildExcelRows(points: FinancialTrendPoint[]): Record<string, unknown>[] {
  return points.map(p => ({
    Period:          p.period,
    Revenue:         p.revenue,
    Growth:          p.revenueGrowthPercent ?? '',
    Expenses:        p.expenses,
    GrossProfit:     p.grossProfit,
    OperatingProfit: p.operatingProfit,
    NetProfit:       p.netProfit,
    RevenueMa3:      p.revenueMovingAverage3,
    NetProfitMa3:    p.netProfitMovingAverage3,
    Cash:            p.cash,
    AR:              p.accountsReceivable,
    AP:              p.accountsPayable,
    WorkingCapital:  p.workingCapital,
    RevenueYtd:      p.revenueYtd,
    NetProfitYtd:    p.netProfitYtd,
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export const FinancialTrendPage = () => {
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const [filter, setFilter] = useState<FinancialTrendFilter>({
    toDate: today(),
    months: 24,
    granularity: 'MONTH',
  });
  const [data, setData] = useState<FinancialTrendDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.getFinancialTrend(filter);
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  if (!hasPermission(Permissions.RPT_FINANCIAL_TREND)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const handleExcel = () => {
    if (!data) return;
    exportToExcel({
      sheetName: 'Financial Trend',
      columns: EXCEL_COLUMNS,
      rows: buildExcelRows(data.points),
      title: 'Financial Trend Analysis',
      subtitle: `${data.companyName} · ${data.fromDate} to ${data.toDate} · ${data.granularity}`,
    }, `FinancialTrend_${data.fromDate}_${data.toDate}`);
  };

  const points = data?.points ?? [];
  const hasPoints = points.length > 0;

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TrendingUp size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Financial Trend Analysis</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onPrint={() => setShowPrint(true)}
          hasData={!!data}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4 space-y-3')}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className={LABEL_CLS}>To Date *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Months</label>
            <select
              value={filter.months ?? 24}
              onChange={e => setFilter(f => ({ ...f, months: Number(e.target.value) }))}
              className={INPUT_CLS}
            >
              {MONTHS_OPTIONS.map(m => (
                <option key={m} value={m}>{m} months</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Granularity</label>
            <select
              value={filter.granularity ?? 'MONTH'}
              onChange={e =>
                setFilter(f => ({ ...f, granularity: e.target.value as AnalyticsGranularity }))
              }
              className={INPUT_CLS}
            >
              {GRANULARITY_OPTIONS.map(g => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id || undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Cost Centre</label>
            <CostCentreSelect
              value={filter.costCenterId}
              onChange={id => setFilter(f => ({ ...f, costCenterId: id || undefined }))}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <RunReportButton
            onClick={() => { void run(); }}
            isLoading={loading}
            disabled={!filter.toDate}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Empty */}
      {!data && !loading && (
        <EmptyState message='Choose a window and click "Run Report" to generate the trend analysis.' />
      )}

      {/* Results */}
      {data && (
        <div className="flex flex-col gap-4">
          {/* Report meta */}
          <div className={cn(CARD_CLS, 'px-4 py-3')}>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Financial Trend — {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
              {' · '}{data.granularity}
              {' · '}{points.length} periods
              {' · '}Generated {fmtDate(data.generatedAt)}
            </div>
          </div>

          {/* CAGR tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CagrTile
              label="Revenue CAGR"
              value={data.revenueCagrPercent}
              note="Compound annual growth rate over the selected window"
            />
            <CagrTile
              label="Net Profit CAGR"
              value={data.netProfitCagrPercent}
              note="Compound annual growth rate over the selected window"
            />
          </div>

          {!hasPoints ? (
            <Section title="Trend">
              <EmptyState message="No periods returned for the selected window." />
            </Section>
          ) : (
            <>
              {/* P&L trend */}
              <Section
                title="Profit & Loss Trend"
                subtitle="Revenue and expenses per period, with net profit and the 3-period revenue moving average"
              >
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={points} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-100 dark:text-gray-800" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCompact} />
                    <ChartTooltip content={<TrendTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="revenue"  name="Revenue"  fill={CHART_COLOURS[0]} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="expenses" name="Expenses" fill={CHART_COLOURS[3]} radius={[3, 3, 0, 0]} />
                    <Line
                      type="monotone"
                      dataKey="netProfit"
                      name="Net Profit"
                      stroke={CHART_COLOURS[1]}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenueMovingAverage3"
                      name="Revenue 3-period moving average"
                      stroke={CHART_COLOURS[4]}
                      strokeWidth={2}
                      strokeDasharray="5 4"
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Section>

              {/* Balance sheet trend */}
              <Section
                title="Balance Sheet Trend"
                subtitle="Cash, receivables, payables and working capital per period"
              >
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={points} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-100 dark:text-gray-800" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCompact} />
                    <ChartTooltip content={<TrendTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="cash"               name="Cash"                stroke={CHART_COLOURS[0]} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="accountsReceivable" name="Accounts Receivable" stroke={CHART_COLOURS[1]} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="accountsPayable"    name="Accounts Payable"    stroke={CHART_COLOURS[3]} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="workingCapital"     name="Working Capital"     stroke={CHART_COLOURS[4]} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Section>

              {/* YTD */}
              <Section
                title="Year to Date"
                subtitle="Cumulative revenue and net profit within each financial year"
              >
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={points} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="ftRevenueYtd" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={CHART_COLOURS[0]} stopOpacity={0.35} />
                        <stop offset="95%" stopColor={CHART_COLOURS[0]} stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="ftNetProfitYtd" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={CHART_COLOURS[1]} stopOpacity={0.35} />
                        <stop offset="95%" stopColor={CHART_COLOURS[1]} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-100 dark:text-gray-800" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCompact} />
                    <ChartTooltip content={<TrendTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area
                      type="monotone"
                      dataKey="revenueYtd"
                      name="Revenue YTD"
                      stroke={CHART_COLOURS[0]}
                      strokeWidth={2}
                      fill="url(#ftRevenueYtd)"
                    />
                    <Area
                      type="monotone"
                      dataKey="netProfitYtd"
                      name="Net Profit YTD"
                      stroke={CHART_COLOURS[1]}
                      strokeWidth={2}
                      fill="url(#ftNetProfitYtd)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Section>
            </>
          )}

          {/* Data table */}
          <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
            <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Trend Detail</h2>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-sm border-collapse min-w-[1500px]">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    <th className={cn(TH_CLS, 'w-28')}>Period</th>
                    <th className={TH_R_CLS}>Revenue</th>
                    <th className={TH_R_CLS}>Growth %</th>
                    <th className={TH_R_CLS}>Expenses</th>
                    <th className={TH_R_CLS}>Gross Profit</th>
                    <th className={TH_R_CLS}>Operating Profit</th>
                    <th className={TH_R_CLS}>Net Profit</th>
                    <th className={TH_R_CLS}>Revenue MA3</th>
                    <th className={TH_R_CLS}>Net Profit MA3</th>
                    <th className={TH_R_CLS}>Cash</th>
                    <th className={TH_R_CLS}>AR</th>
                    <th className={TH_R_CLS}>AP</th>
                    <th className={TH_R_CLS}>Working Capital</th>
                    <th className={TH_R_CLS}>Revenue YTD</th>
                    <th className={TH_R_CLS}>Net Profit YTD</th>
                  </tr>
                </thead>
                <tbody>
                  {!hasPoints ? (
                    <tr>
                      <td colSpan={15} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                        No periods returned for the selected window.
                      </td>
                    </tr>
                  ) : (
                    points.map(p => (
                      <tr
                        key={p.period}
                        className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      >
                        <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          {p.period}
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-900 dark:text-gray-100')}>{fmtAmt(p.revenue)}</td>
                        <td className="px-3 py-2 text-right">
                          <Delta percent={p.revenueGrowthPercent} />
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(p.expenses)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(p.grossProfit)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(p.operatingProfit)}</td>
                        <td
                          className={cn(
                            AMT_CLS, 'font-medium',
                            p.netProfit < 0
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-gray-900 dark:text-gray-100',
                          )}
                        >
                          {fmtAmt(p.netProfit)}
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-500 dark:text-gray-400')}>{fmtAmt(p.revenueMovingAverage3)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-500 dark:text-gray-400')}>{fmtAmt(p.netProfitMovingAverage3)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(p.cash)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(p.accountsReceivable)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(p.accountsPayable)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(p.workingCapital)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(p.revenueYtd)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(p.netProfitYtd)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Metrics the backend declined to compute */}
          <UnavailableMetrics metrics={data.unavailableMetrics} />
        </div>
      )}

      {/* Print preview */}
      {data && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="Financial Trend Analysis"
          orientation="landscape"
        >
          <ReportHeader
            reportTitle="Financial Trend Analysis"
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Company',         value: data.companyName },
              { label: 'Period',          value: `${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}` },
              { label: 'Granularity',     value: data.granularity },
              { label: 'Revenue CAGR',    value: fmtPct(data.revenueCagrPercent) },
              { label: 'Net Profit CAGR', value: fmtPct(data.netProfitCagrPercent) },
            ]}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5px', marginTop: '8px' }}>
            <thead>
              <tr>
                {['Period', 'Revenue', 'Growth %', 'Expenses', 'Gross Profit', 'Operating Profit', 'Net Profit', 'Cash', 'AR', 'AP', 'Working Capital'].map((h, i) => (
                  <th
                    key={h}
                    style={{
                      background: '#1e3a5f', color: 'white', padding: '5px 6px',
                      textAlign: i === 0 ? 'left' : 'right',
                      fontWeight: 700, whiteSpace: 'nowrap', fontSize: '8.5px',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {points.map(p => (
                <tr key={p.period} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '3px 6px', fontWeight: 600 }}>{p.period}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(p.revenue)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtPct(p.revenueGrowthPercent)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(p.expenses)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(p.grossProfit)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(p.operatingProfit)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtAmt(p.netProfit)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(p.cash)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(p.accountsReceivable)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(p.accountsPayable)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(p.workingCapital)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};
